import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useCardsSearchInfiniteQuery } from 'store/services/cards/search/cardsSearchApi';
import { pickAltNames } from 'utils/cardNames';
import HoverOverlay from './Components/HoverOverlay';
import QuickSearchBar from './Components/QuickSearchBar';
import GlassContainer from 'components/GlassContainer/GlassContainer';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { decimalToPoints } from 'utils/decimalUtils';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import './SearchResultsPage.css';

export default function SearchResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Parse URL parameters into search filters
  const searchParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const filters: any = {};
    
    // Text parameters
    if (params.get('name')) filters.name = params.get('name');
    if (params.get('oracleText')) filters.oracleText = params.get('oracleText');
    if (params.get('flavorText')) filters.flavorText = params.get('flavorText');
    if (params.get('typeLine')) filters.typeLine = params.get('typeLine');
    if (params.get('setName')) filters.setName = params.get('setName');
    if (params.get('exactSetName')) filters.exactSetName = params.get('exactSetName') === 'true';
    if (params.get('manaCost')) filters.manaCost = params.get('manaCost');
    
    // Colors parameter (comma-separated)
    if (params.get('colors')) {
      filters.colors = params.get('colors')!.split(',');
    }
    
    // Numeric parameters with comparisons
    if (params.get('cmc')) {
      filters.cmc = parseInt(params.get('cmc')!);
      if (params.get('cmc_comparison')) {
        filters.cmc_comparison = params.get('cmc_comparison') as 'eq' | 'gt' | 'lt';
      }
    }
    if (params.get('power')) {
      filters.power = params.get('power');
      if (params.get('power_comparison')) {
        filters.power_comparison = params.get('power_comparison') as 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
      }
    }
    if (params.get('toughness')) {
      filters.toughness = params.get('toughness');
      if (params.get('toughness_comparison')) {
        filters.toughness_comparison = params.get('toughness_comparison') as 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
      }
    }
    
    // Advanced filters
    if (params.get('artist')) filters.artist = params.get('artist');
    if (params.get('lang')) filters.lang = params.get('lang');
    if (params.get('format')) filters.format = params.get('format');
    if (params.get('formatLegality')) filters.formatLegality = params.get('formatLegality');
    
    return filters;
  }, [location.search]);

  // Fetch search results using infinite query
  const {
    data: infiniteData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useCardsSearchInfiniteQuery(
    searchParams,
    {
      skip: Object.keys(searchParams).length === 0, // Skip if no search params
    }
  );

  // Flatten all pages into a single array of cards
  const allCards = useMemo(() => {
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap(page => page.cards);
  }, [infiniteData]);


  // Infinite scroll functionality - triggers earlier for smoother UX
  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoading || isFetchingNextPage || !hasNextPage || !node) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { 
        threshold: 0.1, // Reduced threshold for earlier triggering
        rootMargin: '200px' // Trigger 200px before element comes into view
      }
    );
    
    observer.observe(node);
    
    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleSearchChange = (value: string) => {
    if (value.trim()) {
      navigate(`/search/results?name=${encodeURIComponent(value)}`);
    }
  };

  const handleSearch = (searchValue?: string) => {
    // Use the directly passed value if available
    if (searchValue && searchValue.trim()) {
      navigate(`/search/results?name=${encodeURIComponent(searchValue)}`);
    }
  };

  const handleCardInteraction = (cardId: string) => {
    if (isMobile) {
      // On mobile, show the overlay on tap (don't toggle)
      setHoveredCard(cardId);
    } else {
      // On desktop, show on hover
      setHoveredCard(cardId);
    }
  };

  const handleCardLeave = () => {
    if (!isMobile) {
      // Only hide on mouse leave for desktop
      setHoveredCard(null);
    }
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="search-results-page-container">
        <GlassContainer className="search-results-glass-container">
          <div className="search-results-header">
            <QuickSearchBar
              placeholder="Search for Magic Cards..."
              onSearchChange={handleSearchChange}
              onSearch={handleSearch}
            />
          </div>
          <div className="search-results-body">
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '60px 0',
              gap: '16px'
            }}>
              <LoadingIndicator />
              <div style={{ color: 'var(--white)', fontSize: '16px' }}>
                Searching database for matching cards...
              </div>
            </div>
          </div>
        </GlassContainer>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="search-results-page-container">
        <GlassContainer className="search-results-glass-container">
          <div className="search-results-header">
            <QuickSearchBar
              placeholder="Search for Magic Cards..."
              onSearchChange={handleSearchChange}
              onSearch={handleSearch}
            />
          </div>
          <div className="search-results-body">
            <div className="error-message">
              Error loading search results. Please try again.
            </div>
          </div>
        </GlassContainer>
      </div>
    );
  }

  // Show message if no search params
  if (Object.keys(searchParams).length === 0) {
    return (
      <div className="search-results-page-container">
        <GlassContainer className="search-results-glass-container">
          <div className="search-results-header">
            <QuickSearchBar
              placeholder="Search for Magic Cards..."
              onSearchChange={handleSearchChange}
              onSearch={handleSearch}
            />
          </div>
          <div className="search-results-body">
            <div className="no-search-message">
              Enter search criteria to find cards.
            </div>
          </div>
        </GlassContainer>
      </div>
    );
  }

  // Generate dynamic SEO title based on search query
  const getSEOTitle = () => {
    if (searchParams.name) {
      return `Search: ${searchParams.name} | Magic Cards for Trade - TradeMagic`;
    }
    if (searchParams.artist) {
      return `Cards by ${searchParams.artist} | Magic Cards for Trade - TradeMagic`;
    }
    if (searchParams.setName) {
      return `${searchParams.setName} Cards | Magic Cards for Trade - TradeMagic`;
    }
    return 'Search Results | Magic Cards for Trade - TradeMagic';
  };

  const getSEODescription = () => {
    if (searchParams.name) {
      return `Find ${searchParams.name} cards available for peer-to-peer trading on TradeMagic. No fees, no middleman. Trade directly with collectors.`;
    }
    return 'Browse Magic: The Gathering cards available for peer-to-peer trading. No fees, no middleman. Find the cards you need and trade directly with collectors.';
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title={getSEOTitle()}
        description={getSEODescription()}
        canonical="https://trademagic.gg/search/results"
      />
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Search Cards', url: 'https://trademagic.gg/search' },
          { name: 'Results', url: 'https://trademagic.gg/search/results' },
        ]}
      />

      <div className="search-results-page-container">
        <GlassContainer className="search-results-glass-container">
          {/* Header with QuickSearchBar */}
          <div className="search-results-header">
          <QuickSearchBar
            placeholder="Search for Magic Cards..."
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
          />
        </div>

        {/* Body with Results Grid */}
        <div className="search-results-body">
          {allCards.length === 0 && !isLoading ? (
            <div className="no-results-message">
              No cards found matching your search criteria.
            </div>
          ) : (
            <>
              <div className="results-count">
                Found {allCards.length} card{allCards.length !== 1 ? 's' : ''}
                {hasNextPage && ' (loading more...)'}
              </div>
              <div className="results-grid">
                {allCards.map((card, index) => {
                  // Handle both scryfallId (backend) and scryfallID (TypeScript interface) casings
                  const scryfallId = (card as any).scryfallId || card.scryfallID;

                  return (
                    <div
                      key={`${card.id}-${index}`}
                      className="card-wrapper"
                      onMouseEnter={() => handleCardInteraction(scryfallId)}
                      onMouseLeave={handleCardLeave}
                      onClick={() => handleCardInteraction(scryfallId)}
                      >
                        {hoveredCard === scryfallId ? (
                          <HoverOverlay
                            title={card.name}
                            altName={pickAltNames({
                              name: card.name,
                              printedName: (card as any).printedName,
                              flavorName: (card as any).flavorName,
                              lang: (card as any).lang,
                            }).primary ?? undefined}
                            cardId={scryfallId}
                            oracleId={card.oracleId || undefined}
                            setName={card.setName}
                            setCode={(card as any).setCode}
                            setIconUrl={(card as any).setIconUrl}
                            collectorNumber={(card as any).collectorNumber}
                            availableInNonFoil={(card as any).availableInNonFoil}
                            availableInFoil={(card as any).availableInFoil}
                            nonFoilPrice={(() => {
                              // Use the new nonFoilPoints field from backend
                              if ((card as any).nonFoilPoints !== undefined && (card as any).nonFoilPoints !== null) {
                                return (card as any).nonFoilPoints;
                              }
                              
                              // Fallback to pointValue for backward compatibility
                              if ((card as any).pointValue !== undefined && (card as any).pointValue !== null) {
                                return (card as any).pointValue;
                              }
                              
                              // Try to extract from price array if available
                              if (Array.isArray(card.price) && card.price.length > 0) {
                                const priceData = card.price[0];
                                if (priceData?.usd) {
                                  const points = decimalToPoints(priceData.usd);
                                  return points !== null ? points : 0;
                                }
                              }
                              
                              // Return 0 if no price data available (will show "???" in UI)
                              return 0;
                            })()}
                            foilPrice={(() => {
                              // Use the new foilPoints field from backend
                              if ((card as any).foilPoints !== undefined && (card as any).foilPoints !== null) {
                                return (card as any).foilPoints;
                              }
                              
                              // Try to extract from price array if available
                              if (Array.isArray(card.price) && card.price.length > 0) {
                                const priceData = card.price[0];
                                if (priceData?.usdFoil) {
                                  const points = decimalToPoints(priceData.usdFoil);
                                  return points !== null ? points : 0;
                                }
                              }
                              
                              // Return 0 if no foil price available (will show "???" in UI)
                              // NEVER estimate foil prices
                              return 0;
                            })()}
                            onClose={isMobile ? () => setHoveredCard(null) : undefined}
                            isMobile={isMobile}
                          />
                        ) : (
                          <img 
                            src={(() => {
                              // Check for double-faced cards
                              const cardFaces = (card as any)?.card_faces || (card as any)?.cardFaces;
                              
                              const possibleImages = [
                                card.imageUris?.normal,
                                card.imageUris?.large,
                                card.imageUris?.small,
                                (card as any)?.image_uris?.normal,
                                (card as any)?.image_uris?.large,
                                (card as any)?.image_uris?.small,
                                // For double-faced cards, try the first face
                                cardFaces?.[0]?.image_uris?.normal,
                                cardFaces?.[0]?.image_uris?.large,
                                cardFaces?.[0]?.imageUris?.normal,
                                cardFaces?.[0]?.imageUris?.large,
                              ];
                              
                              const validImage = possibleImages.find(img => img && img !== '');
                              return validImage || 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
                            })()}
                            alt={card.name} 
                            className="card-image"
                            onError={(e) => {
                              const img = e.currentTarget;
                              console.error('Search result image failed to load:', img.src);
                              
                              // For staging environment, try Scryfall as fallback
                              if (process.env.REACT_APP_ENV === 'staging' && !img.dataset.scryfallAttempted) {
                                img.dataset.scryfallAttempted = 'true';
                                
                                // Extract Scryfall ID from CDN URL
                                const match = img.src.match(/\/([a-f0-9-]{36})\.jpg/);
                                if (match) {
                                  const scryfallId = match[1];
                                  // Use Scryfall URL directly
                                  const scryfallUrl = `https://cards.scryfall.io/normal/front/${scryfallId.slice(0,1)}/${scryfallId.slice(1,2)}/${scryfallId}.jpg`;
                                  console.log('Staging: Trying Scryfall fallback:', scryfallUrl);
                                  img.src = scryfallUrl;
                                  return;
                                }
                              }
                              
                              // Try backend-provided fallback URLs if available
                              const fallbacks = (card as any)?.normal_fallbacks;
                              if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                                img.dataset.fallbackAttempted = 'true';
                                const fallbackUrl = fallbacks[0];
                                console.log('Trying backend fallback:', fallbackUrl);
                                img.src = fallbackUrl;
                                return;
                              }
                              
                              // If all fallbacks failed, use placeholder
                              img.src = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
                            }}
                          />
                        )}
                      </div>
                  );
                })}
              </div>
              
              {/* Infinite scroll trigger and loading message positioned outside the grid */}
              {hasNextPage && (
                <div ref={loadMoreRef} className="load-more-trigger">
                  {isFetchingNextPage && (
                    <div className="loading-more-message">Loading more cards...</div>
                  )}
                </div>
              )}
              
              {!hasNextPage && allCards.length > 0 && (
                <div className="end-of-results-message">
                  End of search results
                </div>
              )}
            </>
          )}
        </div>
        </GlassContainer>
      </div>
    </>
  );
}
